import { ArrowRight, ChevronsDown } from 'lucide-react';
import { type ComponentType, type CSSProperties, type ReactElement, useRef } from 'react';
import { Link } from 'react-router';

import { poseToTransform, trackPose } from '@/shared/lib/sticky-scroll';
import { SCROLL_TRANSFORM_VAR, useStickyScroll } from '@/shared/lib/use-sticky-scroll';
import { cn } from '@/shared/lib/utils';

import { EDU_SPREAD_END, EDU_TRACKS, type EduCardSlot } from '../lib/hero-motion';
import { ChatArt, FlowArt, PracticeArt } from './education-hero-art';

/** SiteHeader 높이(h-16). 고정 프레임이 이 선 아래에 붙는다 */
const STICKY_TOP_PX = 64;

type CurriculumCard = {
  readonly slot: Exclude<EduCardSlot, 'contact'>;
  readonly order: string;
  readonly title: string;
  readonly line: string;
  readonly surface: string;
  readonly Art: ComponentType;
};

/**
 * 카드 셋 = 교육에서 실제로 하는 일, 순서대로. 레퍼런스의 초등·중학·고등처럼 한 방향으로 이어진다.
 * 근거는 이 페이지 FAQ 다 — 원리(LLM·디퓨전)부터 설명하고, 다루는 도구는 ChatGPT·Claude·Gemini 등이며,
 * 실제 업무를 가지고 진행한다. 새 약속을 만들지 않는다.
 */
const CURRICULUM: readonly CurriculumCard[] = [
  {
    slot: 'basics',
    order: '01',
    title: 'AI 이해',
    line: 'AI가 어떻게 답하는지 원리부터 짚습니다',
    surface: 'bg-primary text-on-mint',
    Art: ChatArt,
  },
  {
    slot: 'practice',
    order: '02',
    title: '현장 실습',
    line: '우리 회사 업무에 직접 적용합니다',
    surface: 'bg-ink-surface text-on-ink',
    Art: PracticeArt,
  },
  {
    slot: 'automate',
    order: '03',
    title: '업무 자동화',
    line: '교육이 끝나면 돌아가는 자동화가 남습니다',
    surface: 'bg-mint-soft text-on-mint',
    Art: FlowArt,
  },
];

function cardStyle(slot: EduCardSlot): CSSProperties {
  // 첫 페인트부터 모인 자세로 그린다 — 훅이 붙기 전 한 프레임 동안 한 줄로 튀지 않게
  return {
    [SCROLL_TRANSFORM_VAR]: poseToTransform(trackPose(EDU_TRACKS[slot], 0, EDU_SPREAD_END)),
  } as CSSProperties;
}

/**
 * 카드 자리. 좁은 화면은 한 줄에 한 장(sm 부터 2열), 넓은 화면은 가운데 겹쳐 두고 스크롤로 펼친다.
 * 카드 안 그림이 장면이라 좁은 2×2 정사각형에는 들어가지 않는다 — 그래서 모바일은 가로로 넓힌다.
 *
 * 모바일의 최소 높이: 4:3 은 폭 414px 아래에서 272px 보다 낮아져, 03 카드의 단계 말풍선 세 줄이
 * 설명 글을 덮는다(320px 에서 46px, 375px 에서 7px 겹침 실측). 그 아래로는 비율 대신 높이를 지킨다.
 * 비율이 걸린 칸은 최소 높이를 폭으로 되돌려 계산해 화면 밖(320px 에서 363px)으로 넓어진다 —
 * 그래서 폭을 칸에 묶고(w-full), 목록도 열 폭을 화면에 묶는다(grid-cols-1). 둘 중 하나만으로는 안 막힌다.
 */
const SLOT_CLASS = cn(
  'aspect-[4/3] max-sm:min-h-68 max-sm:w-full sm:aspect-square',
  'lg:absolute lg:top-1/2 lg:left-1/2 lg:w-(--edu-card)',
  'lg:[transform:var(--scroll-transform)] lg:will-change-transform',
);

const FACE_CLASS =
  '@container relative flex size-full flex-col overflow-hidden rounded-[26px] p-6 shadow-[0_24px_48px_-28px_rgb(6_17_11/0.45)]';

function CurriculumCardItem({ card }: { card: CurriculumCard }): ReactElement {
  return (
    <li data-scroll-card={card.slot} style={cardStyle(card.slot)} className={SLOT_CLASS}>
      <div className={cn(FACE_CLASS, card.surface)}>
        <card.Art />
        <span className="relative z-10 font-mono text-[11.5px] font-medium opacity-60">
          {card.order}
        </span>
        {/* 제목이지만 h2 로 두지 않는다 — 페이지 목차(사례·후기·FAQ)에 단계 이름이 섞인다 */}
        <p className="relative z-10 mt-1 text-[clamp(1.5rem,1.1rem+1vw,1.875rem)] font-bold leading-[1.1] tracking-[-0.05em]">
          {card.title}
        </p>
        {/* 폭을 좁혀 오른쪽의 그림과 겹치지 않게 한다. 카드가 272px 보다 작으면(1024~1430px 의
            펼친 행) 그림이 이 줄을 덮는다 — 같은 말이 아래 서비스 소개·진행 과정에 있어 숨긴다.
            기준은 카드 안쪽 폭이라 좌우 여백(p-6)을 뺀 14rem 이다 */}
        <p className="relative z-10 mt-2 max-w-[11em] @max-[14rem]:hidden text-[12.5px] leading-[1.5] opacity-75 lg:text-[13px]">
          {card.line}
        </p>
      </div>
    </li>
  );
}

/**
 * 네 번째 자리 — 문의 카드. 레퍼런스의 알약 묶음 자리를 한 장의 카드로 정리했다.
 * 히어로에는 문의 하나만 둔다(고를 것이 둘이면 둘 다 안 누른다) — 누를 곳은 버튼 하나다.
 */
function ContactCard(): ReactElement {
  return (
    <li data-scroll-card="contact" style={cardStyle('contact')} className={SLOT_CLASS}>
      <div className={cn(FACE_CLASS, 'justify-between bg-ink-surface text-on-ink')}>
        <span
          aria-hidden="true"
          className="-top-10 -right-12 absolute h-32 w-48 bg-primary/15 [transform:skewX(var(--slant))]"
        />
        <div className="relative flex flex-col gap-2">
          <span className="font-mono text-[11.5px] text-primary uppercase tracking-[0.16em]">
            Contact
          </span>
          <p className="text-[clamp(1.125rem,0.9rem+0.6vw,1.375rem)] font-bold leading-[1.35] tracking-[-0.04em]">
            우리 회사에 맞춰
            <br />
            교육을 설계해 드립니다
          </p>
        </div>
        <div className="relative flex flex-col gap-2.5">
          <Link
            to="/education/inquiry"
            className={cn(
              'group inline-flex h-12 items-center justify-center gap-1.5 rounded-full bg-primary text-[15px] font-semibold text-on-mint',
              'transition-colors hover:bg-primary/85',
              'focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-primary',
            )}
          >
            바로 문의하기
            <ArrowRight
              aria-hidden="true"
              className="size-4 transition-transform group-hover:translate-x-0.5"
              strokeWidth={2.2}
            />
          </Link>
          <span className="text-center text-[12px] text-on-ink/60">영업일 2일 안에 답변</span>
        </div>
      </div>
    </li>
  );
}

/**
 * 교육 히어로 — 레퍼런스(두 줄 헤드라인 + 카드 4장)를 옮겼다.
 *
 * 넓은 화면(lg+)에서는 섹션을 화면보다 길게 두고 안쪽 프레임을 고정(sticky)시킨다.
 * 그 길이만큼 스크롤하는 동안 화면 아래의 카드 더미가 떠오르며 한 줄로 펼쳐진다(lib/hero-motion).
 * 좁은 화면은 고정 구간 없이 카드를 쌓는다.
 *
 * 헤드라인은 「무엇을(사내 AI 교육)」과 「어떻게(우리 업무로 배우고 바로 쓴다)」를 한 번에 말한다.
 * 레퍼런스처럼 가는 글씨와 굵은 글씨를 섞어, 무엇과 결과만 굵게 둔다.
 */
export function EducationHero(): ReactElement {
  const sectionRef = useRef<HTMLElement>(null);
  useStickyScroll(sectionRef, {
    stickyTop: STICKY_TOP_PX,
    tracks: EDU_TRACKS,
    spreadEnd: EDU_SPREAD_END,
  });

  return (
    <section
      ref={sectionRef}
      aria-labelledby="edu-hero-heading"
      className="u-slide relative lg:h-[220svh]"
    >
      <div className="flex flex-col lg:sticky lg:top-16 lg:h-[calc(100svh-4rem)] lg:justify-center lg:gap-12 lg:overflow-hidden">
        {/* 슬라이드처럼 넘어간다는 것을 알린다 — 첫 화면에서 카드 더미가 반쯤 잘려 보이는 이유이기도 하다 */}
        <span
          aria-hidden="true"
          className="absolute bottom-5 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-1 font-mono text-[11px] text-muted-foreground tracking-[0.2em] lg:flex"
        >
          SCROLL
          <ChevronsDown className="size-4 motion-safe:animate-bounce" strokeWidth={1.8} />
        </span>
        <div className="u-container flex flex-col items-center gap-5 pt-16 pb-10 text-center lg:p-0">
          <span className="font-mono text-[11px] text-muted-foreground uppercase tracking-[0.18em]">
            Education
          </span>
          <h1
            id="edu-hero-heading"
            className="text-[clamp(1.75rem,1.2rem+2vw,3rem)] font-light leading-[1.32] tracking-[-0.05em]"
          >
            <b className="font-bold">사내 AI 교육,</b>
            <br />
            우리 회사 업무로 배우고 <b className="font-bold">바로 씁니다</b>
          </h1>
          <p className="max-w-[36rem] text-[14.5px] leading-[1.75] text-muted-foreground">
            ChatGPT·Claude 같은 AI 도구를 팀의 실제 업무에 붙이는 법을, 진단부터 실습·정착까지 함께
            설계합니다.
          </p>
        </div>

        {/* 펼친 행의 카드 한 변. 19vw 만 쓰면 1024px 에서 195px 로 작아져 그림이 제목까지 덮는다 —
            그래서 14.375rem(230px) 아래로는 내리지 않는다. 네 장 + 간격(폭의 4.24배)이 1024px 에
            들어가는 최대치다. 34svh 는 낮은 창에서 행이 첫 화면 밖으로 밀리지 않게 하는 상한이다 */}
        <ul
          aria-label="교육 과정"
          className={cn(
            'u-container grid grid-cols-1 gap-3 pb-16 sm:grid-cols-2',
            'lg:relative lg:block lg:h-(--edu-card) lg:p-0',
            'lg:[--edu-card:min(max(19vw,14.375rem),34svh,19rem)]',
          )}
        >
          {CURRICULUM.map((card) => (
            <CurriculumCardItem key={card.slot} card={card} />
          ))}
          <ContactCard />
        </ul>
      </div>
    </section>
  );
}
